/**
 * BoundDropdown — the shared dropdown-trigger primitive used by
 * `WindowFramePicker` for the unit / start-bound / end-bound controls.
 * Extracted (Task §4b-i-fix) purely to keep `WindowFramePicker.tsx` under the
 * ~200-line budget once the ordering-constraint logic landed — no behavior
 * change from the inline version.
 */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import { cn } from '../../../../../lib/utils';

const TRIGGER_CLASS =
  'inline-flex items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 ' +
  'text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ' +
  'cursor-pointer hover:bg-accent hover:text-foreground hover:ring-1 hover:ring-ring/50';

export interface DropdownOption {
  readonly key: string;
  readonly label: string;
  readonly active: boolean;
}

export interface BoundDropdownProps {
  readonly testId: string;
  readonly label: string;
  readonly ariaLabel: string;
  readonly options: readonly DropdownOption[];
  readonly onSelect: (key: string) => void;
}

export function BoundDropdown({ testId, label, ariaLabel, options, onSelect }: BoundDropdownProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger type="button" aria-label={ariaLabel} data-testid={testId} className={TRIGGER_CLASS}>
        {label}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" data-testid={`${testId}-menu`}>
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt.key}
            data-testid={`${testId}-option-${opt.key}`}
            onSelect={() => onSelect(opt.key)}
            className={cn('text-xs', opt.active ? 'font-semibold' : undefined)}
          >
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
